import { cn } from '@/lib/cn';

/**
 * InlineLoader — the small "Loading…" text affordance used in the middle
 * of panels, sidebars, and inline catalogues where a full `LoadingGrid`
 * would be visual overkill.
 *
 * Exists to unify the ad-hoc `<p>Loading…</p>` strings that were scattered
 * across Phase 5-7 components (FilterRail, ReaderEdit, BookPromptManager,
 * AdminBookLabelMultiSelect). It keeps the status role + polite live
 * semantics consistent so assistive tech reliably announces the state.
 */
export interface InlineLoaderProps {
  /** Label announced to screen readers and shown as the visible text. */
  label?: string;
  /** Muted foreground by default; pass `'warm'` for cream-panel parity. */
  tone?: 'muted' | 'warm';
  /** Lays out as a standalone block with centered padding. */
  block?: boolean;
  className?: string;
}

export function InlineLoader({
  label = 'Loading…',
  tone = 'muted',
  block = false,
  className,
}: InlineLoaderProps) {
  return (
    <p
      role="status"
      aria-live="polite"
      className={cn(
        'inline-flex items-center gap-2 text-[12px] italic',
        tone === 'warm' ? 'text-or-slate-500' : 'text-or-slate-500',
        block && 'w-full justify-center px-4 py-6',
        className,
      )}
    >
      <span
        aria-hidden
        className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-or-slate-300 border-t-or-orange-500"
      />
      <span>{label}</span>
    </p>
  );
}
